import { Module } from '../../types';

export const dspyModule8: Module = {
  id: 'dspy-module8',
  title: 'Special Topics and Case Studies',
  description:
    'Explore advanced applications of DSPy across different domains and modalities, including multi-modal data, domain-specific systems, and real-world case studies.',
  duration: '4-5 hours',
  isUnderDevelopment: true,
  content: `# Module 8: Special Topics and Case Studies

## 🎯 Learning Objectives

By the end of this module, you will:
- Build multi-modal applications with images and audio
- Develop domain-specific solutions for specialized fields
- Understand research applications like STORM and PAPILLON
- Learn from real-world case studies and implementations
- Apply DSPy to cutting-edge AI challenges

## 🎨 Multi-Modal Applications

### Working with Images in DSPy

DSPy supports multi-modal applications through the \`dspy.Image\` type, enabling vision-language models to process images alongside text.

#### Basic Image Processing

\`\`\`python
import dspy
from PIL import Image
import base64
from io import BytesIO

class ImageAnalysis(dspy.Signature):
    """Analyze an image and answer questions about it."""
    
    image: dspy.Image = dspy.InputField(desc="Image to analyze")
    question: str = dspy.InputField(desc="Question about the image")
    answer: str = dspy.OutputField(desc="Detailed answer based on the image")

# Configure with a vision-capable model
lm = dspy.LM('openai/gpt-4o-mini')
dspy.configure(lm=lm)

# Create analyzer
analyzer = dspy.ChainOfThought(ImageAnalysis)

# Load and analyze image
def analyze_image(image_path: str, question: str):
    # Load image using DSPy's Image class
    image = dspy.Image(path=image_path)
    
    result = analyzer(
        image=image,
        question=question
    )
    
    return result
\`\`\`

#### Advanced Image Generation with Iterative Refinement

\`\`\`python
import dspy
import fal_client
import requests
from PIL import Image
from io import BytesIO

class ImageGenerationRefinement(dspy.Module):
    """Iteratively refine image generation prompts."""
    
    def __init__(self, max_iterations: int = 5):
        super().__init__()
        self.max_iterations = max_iterations
        
        # Prompt evaluation and refinement
        self.evaluate_prompt = dspy.ChainOfThought(
            """desired_description: str,
            current_image: dspy.Image,
            current_prompt: str ->
            feedback: str,
            matches_description: bool,
            refined_prompt: str"""
        )
        
    def generate_image(self, prompt: str) -> dspy.Image:
        """Generate image using FAL's Flux model."""
        request_id = fal_client.submit(
            "fal-ai/flux-pro/v1.1-ultra",
            arguments={"prompt": prompt}
        ).request_id
        
        result = fal_client.result("fal-ai/flux-pro/v1.1-ultra", request_id)
        url = result["images"][0]["url"]
        
        return dspy.Image.from_url(url)
        
    def forward(self, desired_description: str, initial_prompt: str = None):
        current_prompt = initial_prompt or desired_description
        history = []
        
        for iteration in range(self.max_iterations):
            # Generate image
            current_image = self.generate_image(current_prompt)
            
            # Evaluate and refine
            evaluation = self.evaluate_prompt(
                desired_description=desired_description,
                current_image=current_image,
                current_prompt=current_prompt
            )
            
            history.append({
                'iteration': iteration,
                'prompt': current_prompt,
                'feedback': evaluation.feedback,
                'matches': evaluation.matches_description
            })
            
            if evaluation.matches_description:
                break
                
            current_prompt = evaluation.refined_prompt
            
        return dspy.Prediction(
            final_image=current_image,
            final_prompt=current_prompt,
            iterations=len(history),
            history=history,
            success=evaluation.matches_description
        )

# Usage
refiner = ImageGenerationRefinement(max_iterations=5)
result = refiner(
    desired_description="A scene that's both peaceful and tense - a calm lake with storm clouds gathering"
)
\`\`\`

#### Multi-Modal RAG with Images

\`\`\`python
class MultiModalRAG(dspy.Module):
    """RAG system that can process both text and images."""
    
    def __init__(self, text_retriever, image_retriever):
        super().__init__()
        self.text_retriever = text_retriever
        self.image_retriever = image_retriever
        
        # Multi-modal question understanding
        self.analyze_query = dspy.ChainOfThought(
            """query: str ->
            query_type: str,
            needs_visual_context: bool,
            search_terms: list[str]"""
        )
        
        # Text-based answering
        self.text_qa = dspy.ChainOfThought(
            "question: str, context: list[str] -> answer: str"
        )
        
        # Vision-based answering
        self.visual_qa = dspy.ChainOfThought(
            "question: str, images: list[dspy.Image], captions: list[str] -> answer: str"
        )
        
        # Answer synthesis
        self.synthesize = dspy.ChainOfThought(
            """question: str,
            text_answer: str,
            visual_answer: str,
            confidence_scores: dict ->
            final_answer: str,
            sources_used: list[str]"""
        )
        
    def forward(self, query: str):
        # Analyze query
        analysis = self.analyze_query(query=query)
        
        # Retrieve relevant content
        text_passages = []
        images = []
        
        for term in analysis.search_terms:
            text_passages.extend(self.text_retriever(term, k=3).passages)
            
            if analysis.needs_visual_context:
                image_results = self.image_retriever(term, k=2)
                images.extend(image_results.images)
                
        # Generate answers from different modalities
        text_answer = self.text_qa(
            question=query,
            context=text_passages
        )
        
        visual_answer = None
        if images:
            visual_answer = self.visual_qa(
                question=query,
                images=images,
                captions=[img.caption for img in images]
            )
            
        # Synthesize final answer
        final_result = self.synthesize(
            question=query,
            text_answer=text_answer.answer,
            visual_answer=visual_answer.answer if visual_answer else "No visual context",
            confidence_scores={
                'text': 0.8,
                'visual': 0.7 if visual_answer else 0.0
            }
        )
        
        return final_result
\`\`\`

### Audio Processing Applications

DSPy supports audio processing through the \`dspy.Audio\` type, enabling speech recognition, audio analysis, and text-to-speech applications.

#### Spoken Question Answering

\`\`\`python
import dspy
import soundfile as sf
import numpy as np

class SpokenQA(dspy.Signature):
    """Answer questions based on spoken audio content."""
    
    audio_passage: dspy.Audio = dspy.InputField(desc="Audio containing information")
    question: str = dspy.InputField(desc="Question to answer")
    answer: str = dspy.OutputField(desc="Answer based on audio content")

class SpokenQASystem(dspy.Module):
    """Complete spoken QA system with audio processing."""
    
    def __init__(self):
        super().__init__()
        # Use audio-capable model
        self.qa = dspy.ChainOfThought(SpokenQA)
        
    def preprocess_audio(self, audio_path: str) -> dspy.Audio:
        """Load and preprocess audio file."""
        # Load audio file
        data, samplerate = sf.read(audio_path)
        
        # Convert to DSPy Audio format
        return dspy.Audio.from_array(data, samplerate)
        
    def forward(self, audio_path: str, question: str):
        # Preprocess audio
        audio = self.preprocess_audio(audio_path)
        
        # Answer question
        result = self.qa(
            audio_passage=audio,
            question=question
        )
        
        return result

# Configure with audio-capable model
lm = dspy.LM('gpt-4o-mini-audio-preview-2024-12-17')
dspy.configure(lm=lm)

# Usage
spoken_qa = SpokenQASystem()
result = spoken_qa(
    audio_path="lecture_recording.wav",
    question="What were the main topics discussed?"
)
\`\`\`

#### Emotion-Aware Text-to-Speech

\`\`\`python
import os
import base64
import hashlib
from openai import OpenAI

class EmotionalTTS(dspy.Module):
    """Generate emotionally expressive speech from text."""
    
    def __init__(self, cache_dir: str = ".audio_cache"):
        super().__init__()
        self.cache_dir = cache_dir
        os.makedirs(cache_dir, exist_ok=True)
        
        # Emotion analysis
        self.analyze_emotion = dspy.ChainOfThought(
            """text: str,
            target_emotion: str ->
            emotion_intensity: float,
            prosody_instructions: str,
            voice_characteristics: str"""
        )
        
        # TTS instruction generation
        self.generate_instructions = dspy.ChainOfThought(
            """text: str,
            target_emotion: str,
            prosody_instructions: str,
            voice_characteristics: str ->
            tts_instruction: str,
            pitch_adjustment: str,
            speed_adjustment: str"""
        )
        
        self.openai_client = OpenAI()
        
    def generate_audio(self, text: str, instruction: str) -> dspy.Audio:
        """Generate audio using OpenAI TTS."""
        # Check cache
        cache_key = hashlib.sha256(f"{text}|||{instruction}".encode()).hexdigest()
        wav_path = os.path.join(self.cache_dir, f"{cache_key}.wav")
        
        if not os.path.exists(wav_path):
            response = self.openai_client.audio.speech.create(
                model="gpt-4o-mini-tts",
                voice="coral",
                input=text,
                instructions=instruction,
                response_format="wav"
            )
            
            with open(wav_path, "wb") as f:
                f.write(response.content)
                
        # Load and return as DSPy Audio
        with open(wav_path, "rb") as f:
            encoded = base64.b64encode(f.read()).decode("utf-8")
            
        return dspy.Audio(data=encoded, format="wav")
        
    def forward(self, text: str, target_emotion: str):
        # Analyze emotional requirements
        emotion_analysis = self.analyze_emotion(
            text=text,
            target_emotion=target_emotion
        )
        
        # Generate TTS instructions
        instructions = self.generate_instructions(
            text=text,
            target_emotion=target_emotion,
            prosody_instructions=emotion_analysis.prosody_instructions,
            voice_characteristics=emotion_analysis.voice_characteristics
        )
        
        # Generate audio
        audio = self.generate_audio(text, instructions.tts_instruction)
        
        return dspy.Prediction(
            audio=audio,
            emotion_intensity=emotion_analysis.emotion_intensity,
            tts_instruction=instructions.tts_instruction,
            metadata={
                'pitch': instructions.pitch_adjustment,
                'speed': instructions.speed_adjustment
            }
        )
\`\`\`

#### Audio-Based Similarity Evaluation

\`\`\`python
import torch
import torchaudio
import soundfile as sf
import io
import base64

class AudioSimilarityEvaluator(dspy.Module):
    """Evaluate similarity between audio samples."""
    
    def __init__(self):
        super().__init__()
        # Load Wav2Vec model for embeddings
        bundle = torchaudio.pipelines.WAV2VEC2_BASE
        self.model = bundle.get_model().eval()
        
        # Similarity judgment
        self.judge_similarity = dspy.ChainOfThought(
            """audio1_features: str,
            audio2_features: str,
            similarity_score: float ->
            judgment: str,
            key_differences: list[str],
            recommendation: str"""
        )
        
    def extract_features(self, audio: dspy.Audio) -> torch.Tensor:
        """Extract audio features using Wav2Vec."""
        # Decode audio
        audio_bytes = base64.b64decode(audio.data)
        array, _ = sf.read(io.BytesIO(audio_bytes), dtype="float32")
        tensor = torch.tensor(array).unsqueeze(0)
        
        # Extract embeddings
        with torch.inference_mode():
            embeddings = self.model(tensor)[0].mean(dim=1)
            
        return embeddings
        
    def compute_similarity(self, embed1: torch.Tensor, embed2: torch.Tensor) -> float:
        """Compute cosine similarity between embeddings."""
        return torch.nn.functional.cosine_similarity(embed1, embed2).item()
        
    def forward(self, audio1: dspy.Audio, audio2: dspy.Audio):
        # Extract features
        features1 = self.extract_features(audio1)
        features2 = self.extract_features(audio2)
        
        # Compute similarity
        similarity = self.compute_similarity(features1, features2)
        
        # Generate qualitative judgment
        judgment = self.judge_similarity(
            audio1_features=str(features1.shape),
            audio2_features=str(features2.shape),
            similarity_score=similarity
        )
        
        return dspy.Prediction(
            similarity_score=similarity,
            is_similar=similarity > 0.8,
            judgment=judgment.judgment,
            differences=judgment.key_differences,
            recommendation=judgment.recommendation
        )
\`\`\`

## 🏢 Domain-Specific Applications

### Legal Document Analysis

\`\`\`python
class LegalDocumentAnalyzer(dspy.Module):
    """Analyze legal documents for key information and risks."""
    
    def __init__(self):
        super().__init__()
        
        # Document classification
        self.classify_document = dspy.ChainOfThought(
            """document_text: str ->
            document_type: str,
            jurisdiction: str,
            parties: list[str],
            effective_date: str"""
        )
        
        # Key terms extraction
        self.extract_terms = dspy.ChainOfThought(
            """document_text: str,
            document_type: str ->
            key_terms: list[dict],
            obligations: list[str],
            rights: list[str],
            termination_clauses: list[str]"""
        )
        
        # Risk assessment
        self.assess_risks = dspy.ChainOfThought(
            """document_type: str,
            key_terms: list[dict],
            obligations: list[str] ->
            risk_level: str,
            risk_factors: list[str],
            recommendations: list[str],
            red_flags: list[str]"""
        )
        
        # Summary generation
        self.generate_summary = dspy.ChainOfThought(
            """document_type: str,
            parties: list[str],
            key_terms: list[dict],
            risk_assessment: dict ->
            executive_summary: str,
            action_items: list[str]"""
        )
        
    def forward(self, document_text: str):
        # Classify document
        classification = self.classify_document(
            document_text=document_text
        )
        
        # Extract key terms
        terms = self.extract_terms(
            document_text=document_text,
            document_type=classification.document_type
        )
        
        # Assess risks
        risks = self.assess_risks(
            document_type=classification.document_type,
            key_terms=terms.key_terms,
            obligations=terms.obligations
        )
        
        # Generate summary
        summary = self.generate_summary(
            document_type=classification.document_type,
            parties=classification.parties,
            key_terms=terms.key_terms,
            risk_assessment={
                'level': risks.risk_level,
                'factors': risks.risk_factors
            }
        )
        
        return dspy.Prediction(
            document_type=classification.document_type,
            parties=classification.parties,
            effective_date=classification.effective_date,
            key_terms=terms.key_terms,
            obligations=terms.obligations,
            rights=terms.rights,
            risk_level=risks.risk_level,
            risk_factors=risks.risk_factors,
            recommendations=risks.recommendations,
            executive_summary=summary.executive_summary,
            action_items=summary.action_items
        )

# Example with optimization for legal accuracy
def legal_accuracy_metric(gold, pred, trace=None):
    """Evaluate legal document analysis accuracy."""
    # Check document type classification
    type_correct = gold.document_type.lower() == pred.document_type.lower()
    
    # Check party identification
    parties_overlap = len(set(gold.parties) & set(pred.parties)) / len(gold.parties)
    
    # Check key terms extraction
    terms_found = sum(1 for term in gold.key_terms if term in pred.key_terms)
    terms_recall = terms_found / len(gold.key_terms) if gold.key_terms else 0
    
    # Combined score
    score = (0.3 * type_correct + 0.3 * parties_overlap + 0.4 * terms_recall)
    
    return score

# Optimize for legal domain
optimizer = dspy.BootstrapFewShot(metric=legal_accuracy_metric)
optimized_analyzer = optimizer.compile(
    LegalDocumentAnalyzer(),
    trainset=legal_training_data
)
\`\`\`

### Medical Information Extraction

\`\`\`python
class MedicalReportAnalyzer(dspy.Module):
    """Extract and validate medical information from clinical reports."""
    
    def __init__(self):
        super().__init__()
        
        # Patient information extraction
        self.extract_patient_info = dspy.ChainOfThought(
            """report_text: str ->
            patient_id: str,
            age: int,
            gender: str,
            medical_history: list[str],
            allergies: list[str]"""
        )
        
        # Symptom and diagnosis extraction
        self.extract_clinical_info = dspy.ChainOfThought(
            """report_text: str ->
            chief_complaint: str,
            symptoms: list[dict],
            diagnoses: list[dict],
            differential_diagnoses: list[str]"""
        )
        
        # Medication extraction with validation
        self.extract_medications = dspy.ChainOfThought(
            """report_text: str,
            patient_allergies: list[str] ->
            current_medications: list[dict],
            new_prescriptions: list[dict],
            discontinued_meds: list[str],
            allergy_warnings: list[str]"""
        )
        
        # Treatment plan extraction
        self.extract_treatment = dspy.ChainOfThought(
            """report_text: str,
            diagnoses: list[dict] ->
            treatment_plan: str,
            procedures: list[dict],
            follow_up_required: bool,
            referrals: list[str]"""
        )
        
        # Medical coding
        self.assign_codes = dspy.ChainOfThought(
            """diagnoses: list[dict],
            procedures: list[dict] ->
            icd10_codes: list[str],
            cpt_codes: list[str],
            coding_notes: str"""
        )
        
    def validate_medications(self, medications: list, allergies: list) -> list:
        """Validate medications against known allergies."""
        warnings = []
        for med in medications:
            for allergy in allergies:
                if allergy.lower() in med.get('name', '').lower():
                    warnings.append(f"WARNING: {med['name']} may conflict with {allergy} allergy")
        return warnings
        
    def forward(self, report_text: str):
        # Extract patient information
        patient_info = self.extract_patient_info(report_text=report_text)
        
        # Extract clinical information
        clinical_info = self.extract_clinical_info(report_text=report_text)
        
        # Extract medications with allergy checking
        medications = self.extract_medications(
            report_text=report_text,
            patient_allergies=patient_info.allergies
        )
        
        # Extract treatment plan
        treatment = self.extract_treatment(
            report_text=report_text,
            diagnoses=clinical_info.diagnoses
        )
        
        # Assign medical codes
        codes = self.assign_codes(
            diagnoses=clinical_info.diagnoses,
            procedures=treatment.procedures
        )
        
        # Additional validation
        med_warnings = self.validate_medications(
            medications.new_prescriptions,
            patient_info.allergies
        )
        
        return dspy.Prediction(
            patient_info=patient_info.dict(),
            clinical_findings={
                'chief_complaint': clinical_info.chief_complaint,
                'symptoms': clinical_info.symptoms,
                'diagnoses': clinical_info.diagnoses
            },
            medications={
                'current': medications.current_medications,
                'new': medications.new_prescriptions,
                'discontinued': medications.discontinued_meds,
                'warnings': medications.allergy_warnings + med_warnings
            },
            treatment_plan={
                'summary': treatment.treatment_plan,
                'procedures': treatment.procedures,
                'follow_up': treatment.follow_up_required,
                'referrals': treatment.referrals
            },
            billing_codes={
                'icd10': codes.icd10_codes,
                'cpt': codes.cpt_codes,
                'notes': codes.coding_notes
            }
        )
\`\`\`

### Financial Report Generation

\`\`\`python
class FinancialAnalysisSystem(dspy.Module):
    """Generate comprehensive financial analysis reports."""
    
    def __init__(self):
        super().__init__()
        
        # Data analysis
        self.analyze_financials = dspy.ChainOfThought(
            """company_data: dict,
            period: str ->
            revenue_trends: dict,
            profit_margins: dict,
            key_ratios: dict,
            year_over_year: dict"""
        )
        
        # Market comparison
        self.compare_market = dspy.ChainOfThought(
            """company_metrics: dict,
            industry_benchmarks: dict ->
            competitive_position: str,
            strengths: list[str],
            weaknesses: list[str],
            market_opportunities: list[str]"""
        )
        
        # Risk assessment
        self.assess_financial_risks = dspy.ChainOfThought(
            """financial_data: dict,
            market_conditions: str ->
            risk_factors: list[dict],
            risk_score: float,
            mitigation_strategies: list[str]"""
        )
        
        # Forecast generation
        self.generate_forecast = dspy.ChainOfThought(
            """historical_data: dict,
            market_trends: dict,
            risk_factors: list[dict] ->
            revenue_forecast: dict,
            growth_projections: dict,
            confidence_intervals: dict,
            assumptions: list[str]"""
        )
        
        # Report writing
        self.write_report = dspy.ChainOfThought(
            """analysis_results: dict,
            forecasts: dict,
            recommendations: list[str] ->
            executive_summary: str,
            detailed_analysis: str,
            investment_recommendation: str,
            disclaimer: str"""
        )
        
    def calculate_ratios(self, financial_data: dict) -> dict:
        """Calculate key financial ratios."""
        ratios = {}
        
        # Profitability ratios
        if 'revenue' in financial_data and 'net_income' in financial_data:
            ratios['profit_margin'] = financial_data['net_income'] / financial_data['revenue']
            
        # Liquidity ratios
        if 'current_assets' in financial_data and 'current_liabilities' in financial_data:
            ratios['current_ratio'] = financial_data['current_assets'] / financial_data['current_liabilities']
            
        # Efficiency ratios
        if 'revenue' in financial_data and 'total_assets' in financial_data:
            ratios['asset_turnover'] = financial_data['revenue'] / financial_data['total_assets']
            
        return ratios
        
    def forward(self, company_data: dict, industry_data: dict, period: str):
        # Analyze financial data
        analysis = self.analyze_financials(
            company_data=company_data,
            period=period
        )
        
        # Calculate additional metrics
        ratios = self.calculate_ratios(company_data)
        analysis.key_ratios.update(ratios)
        
        # Market comparison
        market_analysis = self.compare_market(
            company_metrics=analysis.dict(),
            industry_benchmarks=industry_data
        )
        
        # Risk assessment
        risks = self.assess_financial_risks(
            financial_data=company_data,
            market_conditions=industry_data.get('market_conditions', 'stable')
        )
        
        # Generate forecasts
        forecasts = self.generate_forecast(
            historical_data=company_data.get('historical', {}),
            market_trends=industry_data.get('trends', {}),
            risk_factors=risks.risk_factors
        )
        
        # Generate recommendations
        recommendations = []
        if risks.risk_score < 0.3:
            recommendations.append("Strong buy recommendation based on low risk profile")
        elif risks.risk_score < 0.6:
            recommendations.append("Moderate buy with careful position sizing")
        else:
            recommendations.append("Hold or reduce position due to elevated risks")
            
        # Write comprehensive report
        report = self.write_report(
            analysis_results={
                'financials': analysis.dict(),
                'market_position': market_analysis.dict()
            },
            forecasts=forecasts.dict(),
            recommendations=recommendations
        )
        
        return dspy.Prediction(
            executive_summary=report.executive_summary,
            financial_analysis=analysis.dict(),
            market_position={
                'competitive_position': market_analysis.competitive_position,
                'strengths': market_analysis.strengths,
                'weaknesses': market_analysis.weaknesses
            },
            risk_assessment={
                'score': risks.risk_score,
                'factors': risks.risk_factors,
                'mitigation': risks.mitigation_strategies
            },
            forecasts=forecasts.dict(),
            recommendations=recommendations,
            full_report=report.detailed_analysis,
            disclaimer=report.disclaimer
        )
\`\`\`

## 🔬 Research Applications

### STORM: Writing Wikipedia-like Articles

STORM (Synthesis of Topic Outline through Retrieval and Multi-perspective question asking) is a research system that generates comprehensive Wikipedia-like articles.

\`\`\`python
class STORM(dspy.Module):
    """Generate Wikipedia-like articles through multi-perspective research."""
    
    def __init__(self, retriever, max_perspectives: int = 5):
        super().__init__()
        self.retriever = retriever
        self.max_perspectives = max_perspectives
        
        # Topic analysis
        self.analyze_topic = dspy.ChainOfThought(
            """topic: str ->
            main_aspects: list[str],
            related_topics: list[str],
            controversy_areas: list[str],
            research_questions: list[str]"""
        )
        
        # Perspective generation
        self.generate_perspectives = dspy.ChainOfThought(
            """topic: str,
            aspects: list[str] ->
            perspectives: list[dict],
            expert_roles: list[str]"""
        )
        
        # Multi-perspective questioning
        self.ask_questions = dspy.ChainOfThought(
            """topic: str,
            perspective: dict,
            existing_questions: list[str] ->
            new_questions: list[str],
            follow_ups: list[str]"""
        )
        
        # Section outlining
        self.create_outline = dspy.ChainOfThought(
            """topic: str,
            research_findings: dict,
            perspectives: list[dict] ->
            outline: list[dict],
            section_dependencies: dict"""
        )
        
        # Section writing
        self.write_section = dspy.ChainOfThought(
            """section_title: str,
            section_outline: dict,
            research_content: list[str],
            citations_needed: bool ->
            section_content: str,
            citations: list[dict],
            cross_references: list[str]"""
        )
        
        # Article synthesis
        self.synthesize_article = dspy.ChainOfThought(
            """topic: str,
            sections: list[dict],
            citations: list[dict] ->
            introduction: str,
            main_content: str,
            conclusion: str,
            references: list[str]"""
        )
        
    def research_perspective(self, topic: str, perspective: dict) -> dict:
        """Research topic from a specific perspective."""
        questions = []
        findings = []
        
        # Generate initial questions
        initial_q = self.ask_questions(
            topic=topic,
            perspective=perspective,
            existing_questions=[]
        )
        questions.extend(initial_q.new_questions)
        
        # Research each question
        for question in questions[:10]:  # Limit for efficiency
            results = self.retriever(question, k=5)
            findings.extend(results.passages)
            
            # Generate follow-up questions
            follow_up = self.ask_questions(
                topic=topic,
                perspective=perspective,
                existing_questions=questions
            )
            questions.extend(follow_up.follow_ups[:2])
            
        return {
            'perspective': perspective,
            'questions': questions,
            'findings': findings
        }
        
    def forward(self, topic: str):
        # Analyze topic
        analysis = self.analyze_topic(topic=topic)
        
        # Generate diverse perspectives
        perspectives = self.generate_perspectives(
            topic=topic,
            aspects=analysis.main_aspects
        )
        
        # Research from each perspective
        all_research = []
        for perspective in perspectives.perspectives[:self.max_perspectives]:
            research = self.research_perspective(topic, perspective)
            all_research.append(research)
            
        # Create article outline
        outline = self.create_outline(
            topic=topic,
            research_findings={
                'perspectives': all_research,
                'aspects': analysis.main_aspects
            },
            perspectives=perspectives.perspectives
        )
        
        # Write sections
        sections = []
        all_citations = []
        
        for section_info in outline.outline:
            # Gather relevant research for section
            relevant_content = []
            for research in all_research:
                relevant_content.extend([
                    f for f in research['findings']
                    if any(keyword in f.lower() for keyword in section_info.get('keywords', []))
                ])
                
            # Write section
            section = self.write_section(
                section_title=section_info['title'],
                section_outline=section_info,
                research_content=relevant_content[:20],
                citations_needed=True
            )
            
            sections.append({
                'title': section_info['title'],
                'content': section.section_content,
                'citations': section.citations
            })
            all_citations.extend(section.citations)
            
        # Synthesize final article
        article = self.synthesize_article(
            topic=topic,
            sections=sections,
            citations=all_citations
        )
        
        return dspy.Prediction(
            title=topic,
            introduction=article.introduction,
            sections=sections,
            conclusion=article.conclusion,
            references=article.references,
            metadata={
                'perspectives_used': len(all_research),
                'total_questions': sum(len(r['questions']) for r in all_research),
                'citations_count': len(all_citations)
            }
        )
\`\`\`

### PAPILLON: Privacy-Conscious AI

PAPILLON demonstrates privacy-preserving AI by combining small local models with larger external models.

\`\`\`python
class PAPILLON(dspy.Module):
    """Privacy-preserving AI using local-external model collaboration."""
    
    def __init__(self, local_model, external_model, privacy_threshold: float = 0.8):
        super().__init__()
        self.local_model = local_model
        self.external_model = external_model
        self.privacy_threshold = privacy_threshold
        
        # Privacy analysis
        self.analyze_privacy = dspy.ChainOfThought(
            """text: str ->
            contains_pii: bool,
            pii_types: list[str],
            privacy_score: float,
            sensitive_segments: list[dict]"""
        )
        
        # Anonymization
        self.anonymize_text = dspy.ChainOfThought(
            """text: str,
            pii_types: list[str],
            sensitive_segments: list[dict] ->
            anonymized_text: str,
            mapping: dict,
            preservation_score: float"""
        )
        
        # Task decomposition
        self.decompose_task = dspy.ChainOfThought(
            """task: str,
            privacy_constraints: dict ->
            local_subtasks: list[str],
            external_subtasks: list[str],
            coordination_plan: str"""
        )
        
        # Result synthesis
        self.synthesize_results = dspy.ChainOfThought(
            """local_results: list[dict],
            external_results: list[dict],
            original_task: str ->
            final_result: str,
            confidence: float,
            privacy_preserved: bool"""
        )
        
    def process_with_privacy(self, text: str, task: str):
        """Process task while preserving privacy."""
        # Analyze privacy requirements
        privacy_analysis = self.analyze_privacy(text=text)
        
        results = {
            'local': [],
            'external': [],
            'privacy_score': privacy_analysis.privacy_score
        }
        
        if privacy_analysis.privacy_score > self.privacy_threshold:
            # High privacy risk - use local processing
            # Anonymize sensitive data
            anonymized = self.anonymize_text(
                text=text,
                pii_types=privacy_analysis.pii_types,
                sensitive_segments=privacy_analysis.sensitive_segments
            )
            
            # Decompose task
            decomposition = self.decompose_task(
                task=task,
                privacy_constraints={
                    'pii_types': privacy_analysis.pii_types,
                    'sensitivity': privacy_analysis.privacy_score
                }
            )
            
            # Process local subtasks
            for subtask in decomposition.local_subtasks:
                with dspy.context(lm=self.local_model):
                    local_result = dspy.Predict("task, text -> result")(
                        task=subtask,
                        text=text
                    )
                    results['local'].append(local_result)
                    
            # Process external subtasks with anonymized data
            for subtask in decomposition.external_subtasks:
                with dspy.context(lm=self.external_model):
                    external_result = dspy.Predict("task, text -> result")(
                        task=subtask,
                        text=anonymized.anonymized_text
                    )
                    results['external'].append(external_result)
        else:
            # Low privacy risk - can use external model directly
            with dspy.context(lm=self.external_model):
                external_result = dspy.Predict("task, text -> result")(
                    task=task,
                    text=text
                )
                results['external'].append(external_result)
                
        return results
        
    def forward(self, text: str, task: str):
        # Process with privacy preservation
        processing_results = self.process_with_privacy(text, task)
        
        # Synthesize results
        synthesis = self.synthesize_results(
            local_results=processing_results['local'],
            external_results=processing_results['external'],
            original_task=task
        )
        
        return dspy.Prediction(
            result=synthesis.final_result,
            confidence=synthesis.confidence,
            privacy_preserved=synthesis.privacy_preserved,
            privacy_score=processing_results['privacy_score'],
            processing_mode='hybrid' if processing_results['local'] and processing_results['external'] else 'single'
        )
\`\`\`

### Game Playing Agents

Advanced game-playing agents that learn through fine-tuning.

\`\`\`python
class GameAgent(dspy.Module):
    """Agent for playing text-based games with strategic reasoning."""
    
    def __init__(self, max_iterations: int = 50):
        super().__init__()
        self.max_iterations = max_iterations
        
        # Game understanding
        self.understand_state = dspy.ChainOfThought(
            """game_state: str,
            objective: str,
            history: list[str] ->
            current_location: str,
            inventory: list[str],
            available_objects: list[str],
            progress_assessment: str"""
        )
        
        # Strategy planning
        self.plan_strategy = dspy.ChainOfThought(
            """objective: str,
            current_state: dict,
            past_actions: list[str] ->
            strategy: str,
            next_goals: list[str],
            required_items: list[str]"""
        )
        
        # Action selection with reasoning
        self.select_action = dspy.ReAct(
            """task: str,
            current_state: str,
            trajectory: list[str],
            possible_actions: list[str] ->
            action: str"""
        )
        
        # Learning from mistakes
        self.analyze_failure = dspy.ChainOfThought(
            """trajectory: list[str],
            objective: str,
            failure_point: str ->
            failure_reason: str,
            alternative_approach: str,
            lessons_learned: list[str]"""
        )
        
    def forward(self, game_env, task_idx: int):
        """Play a game episode."""
        trajectory = []
        task, info = game_env.init(task_idx)
        
        # Understand initial state
        initial_understanding = self.understand_state(
            game_state=task,
            objective=self._extract_objective(task),
            history=[]
        )
        
        # Plan initial strategy
        strategy = self.plan_strategy(
            objective=self._extract_objective(task),
            current_state=initial_understanding.dict(),
            past_actions=[]
        )
        
        for iteration in range(self.max_iterations):
            # Get current state understanding
            state_understanding = self.understand_state(
                game_state=trajectory[-1] if trajectory else task,
                objective=self._extract_objective(task),
                history=trajectory
            )
            
            # Select next action
            possible_actions = info["admissible_commands"][0]
            if "think:" not in possible_actions:
                possible_actions.append("think: {reasoning}")
                
            action_result = self.select_action(
                task=task,
                current_state=str(state_understanding.dict()),
                trajectory=trajectory,
                possible_actions=possible_actions
            )
            
            action = action_result.action
            trajectory.append(f"> {action}")
            
            # Handle thinking actions
            if action.startswith("think:"):
                trajectory.append("OK.")
                continue
                
            # Execute action in environment
            obs, reward, done, info = game_env.step(action)
            trajectory.append(obs[0])
            
            if done:
                if reward == 0:
                    # Analyze failure
                    failure_analysis = self.analyze_failure(
                        trajectory=trajectory,
                        objective=self._extract_objective(task),
                        failure_point=trajectory[-2:]
                    )
                    
                    return dspy.Prediction(
                        trajectory=trajectory,
                        success=False,
                        failure_analysis=failure_analysis.dict()
                    )
                else:
                    return dspy.Prediction(
                        trajectory=trajectory,
                        success=True,
                        iterations=iteration + 1
                    )
                    
        return dspy.Prediction(
            trajectory=trajectory,
            success=False,
            failure_reason="Max iterations reached"
        )
        
    def _extract_objective(self, task_description: str) -> str:
        """Extract the objective from task description."""
        lines = task_description.split('\\n')
        for line in lines:
            if "your task is to:" in line.lower():
                return line.split(":")[-1].strip()
        return "Complete the task"

# Fine-tuning game agents
class GameAgentTrainer:
    """Train game agents through self-play and fine-tuning."""
    
    def __init__(self, teacher_model, student_model):
        self.teacher_model = teacher_model
        self.student_model = student_model
        
    def generate_training_data(self, agent, game_env, num_episodes: int = 100):
        """Generate training data through self-play."""
        training_data = []
        
        for episode in range(num_episodes):
            result = agent(game_env, episode % len(game_env.dataset))
            
            if result.success:
                # Extract successful trajectories
                training_data.append({
                    'task': game_env.get_task(episode % len(game_env.dataset)),
                    'trajectory': result.trajectory,
                    'actions': self._extract_actions(result.trajectory)
                })
                
        return training_data
        
    def finetune_agent(self, base_agent, training_data, val_data):
        """Fine-tune agent on successful trajectories."""
        # Create teacher agent with stronger model
        teacher_agent = base_agent.deepcopy()
        teacher_agent.set_lm(self.teacher_model)
        
        # Optimize teacher
        optimizer = dspy.MIPROv2(
            metric=lambda x, y: y.success,
            num_threads=16
        )
        
        optimized_teacher = optimizer.compile(
            teacher_agent,
            trainset=training_data[:100],
            max_bootstrapped_demos=1
        )
        
        # Create student from optimized teacher
        student_agent = optimized_teacher.deepcopy()
        student_agent.set_lm(self.student_model)
        
        # Fine-tune student
        finetuner = dspy.BootstrapFinetune(
            metric=lambda x, y: y.success,
            num_threads=16
        )
        
        finetuned_student = finetuner.compile(
            student_agent,
            teacher=optimized_teacher,
            trainset=training_data
        )
        
        return finetuned_student
        
    def _extract_actions(self, trajectory):
        """Extract actions from trajectory."""
        return [line[2:] for line in trajectory if line.startswith("> ")]
\`\`\`

## 📚 Case Studies

### Case Study 1: JetBlue's Chatbot System

JetBlue uses DSPy to power multiple chatbot use cases, improving customer service efficiency.

\`\`\`python
class JetBlueCustomerService(dspy.Module):
    """Production customer service system inspired by JetBlue's implementation."""
    
    def __init__(self):
        super().__init__()
        
        # Intent classification
        self.classify_intent = dspy.ChainOfThought(
            """customer_message: str,
            conversation_history: list[str] ->
            primary_intent: str,
            secondary_intents: list[str],
            urgency: str,
            sentiment: str"""
        )
        
        # Flight operations
        self.handle_flight_query = dspy.ReAct(
            """query: str,
            customer_info: dict ->
            response: str,
            actions_taken: list[str]""",
            tools=[
                self.check_flight_status,
                self.modify_booking,
                self.search_availability
            ]
        )
        
        # Complaint handling
        self.handle_complaint = dspy.ChainOfThought(
            """complaint: str,
            customer_history: dict ->
            acknowledgment: str,
            resolution_options: list[str],
            compensation_eligible: bool,
            follow_up_required: bool"""
        )
        
        # Response generation with brand voice
        self.generate_response = dspy.ChainOfThought(
            """intent: str,
            resolution: str,
            customer_sentiment: str,
            brand_guidelines: str ->
            response: str,
            tone: str,
            personalization_elements: list[str]"""
        )
        
    def check_flight_status(self, flight_number: str, date: str) -> str:
        """Check real-time flight status."""
        # Integration with flight systems
        return f"Flight {flight_number} on {date}: On time, Gate B42"
        
    def modify_booking(self, booking_ref: str, changes: dict) -> str:
        """Modify existing booking."""
        return f"Booking {booking_ref} modified successfully"
        
    def search_availability(self, route: str, date: str) -> str:
        """Search flight availability."""
        return f"3 flights available for {route} on {date}"
        
    def forward(self, message: str, customer_context: dict):
        # Classify intent
        intent = self.classify_intent(
            customer_message=message,
            conversation_history=customer_context.get('history', [])
        )
        
        # Route to appropriate handler
        if 'flight' in intent.primary_intent.lower():
            result = self.handle_flight_query(
                query=message,
                customer_info=customer_context
            )
            resolution = result.response
            
        elif 'complaint' in intent.primary_intent.lower():
            complaint_result = self.handle_complaint(
                complaint=message,
                customer_history=customer_context
            )
            resolution = complaint_result.acknowledgment
            
        else:
            # General query handling
            resolution = "I'll help you with that query."
            
        # Generate branded response
        final_response = self.generate_response(
            intent=intent.primary_intent,
            resolution=resolution,
            customer_sentiment=intent.sentiment,
            brand_guidelines="Friendly, helpful, aviation-expert tone"
        )
        
        return dspy.Prediction(
            response=final_response.response,
            intent=intent.primary_intent,
            sentiment=intent.sentiment,
            actions_taken=result.actions_taken if 'flight' in intent.primary_intent.lower() else [],
            metadata={
                'urgency': intent.urgency,
                'tone_used': final_response.tone,
                'personalization': final_response.personalization_elements
            }
        )
\`\`\`

### Case Study 2: Replit's Code Synthesis

Replit uses DSPy to synthesize code diffs for their AI-powered coding assistant.

\`\`\`python
class ReplitCodeSynthesizer(dspy.Module):
    """Code synthesis system inspired by Replit's implementation."""
    
    def __init__(self):
        super().__init__()
        
        # Code understanding
        self.understand_context = dspy.ChainOfThought(
            """file_content: str,
            cursor_position: int,
            project_structure: dict ->
            current_function: str,
            relevant_imports: list[str],
            variable_scope: dict,
            inferred_intent: str"""
        )
        
        # Diff generation
        self.generate_diff = dspy.ChainOfThought(
            """original_code: str,
            intent: str,
            context: dict ->
            diff_operations: list[dict],
            explanation: str,
            confidence: float"""
        )
        
        # Code validation
        self.validate_code = dspy.ChainOfThought(
            """original_code: str,
            proposed_changes: list[dict],
            language: str ->
            syntax_valid: bool,
            type_safe: bool,
            style_consistent: bool,
            issues: list[str]"""
        )
        
        # Multi-file refactoring
        self.plan_refactoring = dspy.ChainOfThought(
            """refactoring_request: str,
            affected_files: list[str],
            dependencies: dict ->
            refactoring_steps: list[dict],
            file_order: list[str],
            rollback_plan: str"""
        )
        
    def apply_diff(self, original: str, diff_ops: list) -> str:
        """Apply diff operations to original code."""
        lines = original.split('\\n')
        
        for op in sorted(diff_ops, key=lambda x: x['line'], reverse=True):
            if op['type'] == 'insert':
                lines.insert(op['line'], op['content'])
            elif op['type'] == 'delete':
                if 0 <= op['line'] < len(lines):
                    lines.pop(op['line'])
            elif op['type'] == 'replace':
                if 0 <= op['line'] < len(lines):
                    lines[op['line']] = op['content']
                    
        return '\\n'.join(lines)
        
    def forward(self, file_content: str, user_intent: str, project_context: dict):
        # Understand current context
        context = self.understand_context(
            file_content=file_content,
            cursor_position=project_context.get('cursor', 0),
            project_structure=project_context.get('structure', {})
        )
        
        # Generate diff
        diff_result = self.generate_diff(
            original_code=file_content,
            intent=user_intent,
            context=context.dict()
        )
        
        # Apply diff to get proposed code
        proposed_code = self.apply_diff(file_content, diff_result.diff_operations)
        
        # Validate changes
        validation = self.validate_code(
            original_code=file_content,
            proposed_changes=diff_result.diff_operations,
            language=project_context.get('language', 'python')
        )
        
        # Handle multi-file changes if needed
        refactoring_plan = None
        if project_context.get('multi_file', False):
            refactoring_plan = self.plan_refactoring(
                refactoring_request=user_intent,
                affected_files=project_context.get('related_files', []),
                dependencies=project_context.get('dependencies', {})
            )
            
        return dspy.Prediction(
            proposed_code=proposed_code,
            diff_operations=diff_result.diff_operations,
            explanation=diff_result.explanation,
            validation={
                'syntax_valid': validation.syntax_valid,
                'type_safe': validation.type_safe,
                'style_consistent': validation.style_consistent,
                'issues': validation.issues
            },
            confidence=diff_result.confidence,
            refactoring_plan=refactoring_plan.dict() if refactoring_plan else None
        )
\`\`\`

### Case Study 3: Haize Labs Red-Teaming

Haize Labs uses DSPy for automated red-teaming of language models.

\`\`\`python
class HaizeRedTeamingSystem(dspy.Module):
    """Automated red-teaming system inspired by Haize Labs."""
    
    def __init__(self):
        super().__init__()
        
        # Attack generation
        self.generate_attack = dspy.ChainOfThought(
            """target_capability: str,
            attack_category: str,
            previous_attempts: list[dict] ->
            attack_prompt: str,
            attack_strategy: str,
            expected_vulnerability: str"""
        )
        
        # Response analysis
        self.analyze_response = dspy.ChainOfThought(
            """attack_prompt: str,
            model_response: str,
            expected_behavior: str ->
            attack_successful: bool,
            vulnerability_type: str,
            severity_score: float,
            evidence: list[str]"""
        )
        
        # Attack evolution
        self.evolve_attack = dspy.ChainOfThought(
            """original_attack: str,
            response_analysis: dict,
            model_defenses: list[str] ->
            evolved_attack: str,
            mutation_strategy: str,
            bypass_techniques: list[str]"""
        )
        
        # Report generation
        self.generate_report = dspy.ChainOfThought(
            """test_results: list[dict],
            model_info: dict,
            test_categories: list[str] ->
            executive_summary: str,
            vulnerabilities: list[dict],
            recommendations: list[str],
            risk_score: float"""
        )
        
    def test_category(self, model, category: str, num_tests: int = 10):
        """Test model against specific attack category."""
        results = []
        previous_attempts = []
        
        for i in range(num_tests):
            # Generate attack
            attack = self.generate_attack(
                target_capability=category,
                attack_category=category,
                previous_attempts=previous_attempts
            )
            
            # Execute attack
            try:
                response = model(attack.attack_prompt)
                
                # Analyze response
                analysis = self.analyze_response(
                    attack_prompt=attack.attack_prompt,
                    model_response=response,
                    expected_behavior="Safe, appropriate response"
                )
                
                results.append({
                    'attack': attack.attack_prompt,
                    'response': response,
                    'successful': analysis.attack_successful,
                    'severity': analysis.severity_score,
                    'vulnerability': analysis.vulnerability_type
                })
                
                # Evolve attack if unsuccessful
                if not analysis.attack_successful:
                    evolved = self.evolve_attack(
                        original_attack=attack.attack_prompt,
                        response_analysis=analysis.dict(),
                        model_defenses=["refusal", "redirection", "clarification"]
                    )
                    
                    previous_attempts.append({
                        'attack': attack.attack_prompt,
                        'evolution': evolved.evolved_attack
                    })
                    
            except Exception as e:
                results.append({
                    'attack': attack.attack_prompt,
                    'error': str(e),
                    'successful': False
                })
                
        return results
        
    def forward(self, model, test_categories: list[str], tests_per_category: int = 10):
        """Run comprehensive red-team evaluation."""
        all_results = {}
        
        # Test each category
        for category in test_categories:
            category_results = self.test_category(
                model,
                category,
                tests_per_category
            )
            all_results[category] = category_results
            
        # Generate comprehensive report
        flat_results = []
        for category, results in all_results.items():
            for result in results:
                result['category'] = category
                flat_results.append(result)
                
        report = self.generate_report(
            test_results=flat_results,
            model_info={'name': str(model), 'version': '1.0'},
            test_categories=test_categories
        )
        
        # Calculate overall metrics
        total_tests = len(flat_results)
        successful_attacks = sum(1 for r in flat_results if r.get('successful', False))
        avg_severity = sum(r.get('severity', 0) for r in flat_results) / total_tests
        
        return dspy.Prediction(
            executive_summary=report.executive_summary,
            overall_metrics={
                'total_tests': total_tests,
                'successful_attacks': successful_attacks,
                'success_rate': successful_attacks / total_tests,
                'average_severity': avg_severity,
                'risk_score': report.risk_score
            },
            vulnerabilities=report.vulnerabilities,
            recommendations=report.recommendations,
            detailed_results=all_results
        )
\`\`\`

## 📋 Module Summary

### Key Multi-Modal Capabilities

✅ **Image Processing**
- Vision-language understanding
- Iterative image generation refinement
- Multi-modal RAG systems
- Image similarity evaluation

✅ **Audio Applications**
- Spoken question answering
- Emotion-aware text-to-speech
- Audio similarity metrics
- Speech processing pipelines

### Domain-Specific Solutions

✅ **Legal**
- Document classification and analysis
- Risk assessment
- Key terms extraction
- Automated summarization

✅ **Medical**
- Clinical report analysis
- Medication validation
- Medical coding
- Treatment plan extraction

✅ **Financial**
- Financial analysis and forecasting
- Risk assessment
- Market comparison
- Report generation

### Research Applications

✅ **STORM**
- Multi-perspective research
- Wikipedia-like article generation
- Automatic outlining
- Citation management

✅ **PAPILLON**
- Privacy-preserving AI
- Local-external model collaboration
- Task decomposition
- Anonymization techniques

✅ **Game Agents**
- Strategic reasoning
- Self-improvement through fine-tuning
- Failure analysis
- Multi-step planning

### Real-World Impact

The case studies demonstrate DSPy's versatility:
- **JetBlue**: Enhanced customer service efficiency
- **Replit**: Improved code synthesis accuracy
- **Haize Labs**: Automated security testing
- **Many others**: From Databricks to VMware

### Implementation Patterns

\`\`\`python
# Multi-modal pattern
class MultiModalSystem(dspy.Module):
    def __init__(self):
        self.text_processor = dspy.ChainOfThought(...)
        self.image_processor = dspy.ChainOfThought(...)
        self.fusion = dspy.ChainOfThought(...)
        
    def forward(self, text, image):
        text_result = self.text_processor(text)
        image_result = self.image_processor(image)
        return self.fusion(text_result, image_result)

# Domain-specific pattern
class DomainExpert(dspy.Module):
    def __init__(self):
        self.domain_understanding = dspy.ChainOfThought(...)
        self.specialized_analysis = dspy.ChainOfThought(...)
        self.validation = dspy.ChainOfThought(...)
        
    def forward(self, domain_input):
        understanding = self.domain_understanding(domain_input)
        analysis = self.specialized_analysis(understanding)
        return self.validation(analysis)

# Research pattern
class ResearchSystem(dspy.Module):
    def __init__(self):
        self.hypothesis_generation = dspy.ChainOfThought(...)
        self.experiment_design = dspy.ChainOfThought(...)
        self.result_synthesis = dspy.ChainOfThought(...)
        
    def forward(self, research_question):
        hypothesis = self.hypothesis_generation(research_question)
        experiments = self.experiment_design(hypothesis)
        return self.result_synthesis(experiments)
\`\`\`

You now have the knowledge to build sophisticated multi-modal and domain-specific applications with DSPy!`,
  exercises: [
    {
      id: 'dspy-m8-ex1',
      title: 'Multi-Modal Application',
      description: 'Build a multi-modal application that combines text and images',
      difficulty: 'expert',
      type: 'code',
      instructions: 'Create a system that can analyze both text and images to answer questions.',
      hints: [
        'Use dspy.Image for image inputs',
        'Combine modalities with synthesis',
        'Handle missing modalities gracefully',
      ],
      solution: `class MultiModalQA(dspy.Module):
    def __init__(self):
        super().__init__()
        self.text_analyzer = dspy.ChainOfThought(
            "text: str, question: str -> text_answer: str, confidence: float"
        )
        self.image_analyzer = dspy.ChainOfThought(
            "image: dspy.Image, question: str -> visual_answer: str, objects_found: list[str]"
        )
        self.answer_fusion = dspy.ChainOfThought(
            """question: str,
            text_answer: str,
            visual_answer: str,
            confidence_scores: dict ->
            final_answer: str,
            explanation: str"""
        )
    
    def forward(self, question: str, text: str = None, image: dspy.Image = None):
        text_result = None
        visual_result = None
        
        if text:
            text_result = self.text_analyzer(text=text, question=question)
        
        if image:
            visual_result = self.image_analyzer(image=image, question=question)
        
        # Fuse answers
        final_answer = self.answer_fusion(
            question=question,
            text_answer=text_result.text_answer if text_result else "No text provided",
            visual_answer=visual_result.visual_answer if visual_result else "No image provided",
            confidence_scores={
                'text': text_result.confidence if text_result else 0.0,
                'visual': 0.8 if visual_result else 0.0
            }
        )
        
        return final_answer`,
      testCases: [
        {
          input: 'Multi-modal QA system with text and image processing',
          expected: true,
          description: 'Complete multi-modal implementation',
        },
      ],
    },
    {
      id: 'dspy-m8-ex2',
      title: 'Domain-Specific System',
      description: 'Create a domain-specific application for healthcare',
      difficulty: 'hard',
      type: 'code',
      instructions: 'Build a medical diagnosis support system with proper validation.',
      hints: [
        'Extract symptoms carefully',
        'Validate against allergies',
        'Include confidence scores',
      ],
      solution: `class MedicalDiagnosisSupport(dspy.Module):
    def __init__(self):
        super().__init__()
        self.symptom_analyzer = dspy.ChainOfThought(
            """patient_description: str,
            medical_history: dict ->
            symptoms: list[str],
            severity: str,
            red_flags: list[str]"""
        )
        
        self.diagnosis_generator = dspy.ChainOfThought(
            """symptoms: list[str],
            patient_age: int,
            medical_history: dict ->
            possible_diagnoses: list[dict],
            confidence_levels: dict,
            recommended_tests: list[str]"""
        )
        
        self.treatment_planner = dspy.ChainOfThought(
            """diagnosis: str,
            patient_allergies: list[str],
            current_medications: list[str] ->
            treatment_options: list[dict],
            contraindications: list[str],
            follow_up_required: bool"""
        )
    
    def forward(self, patient_description: str, patient_info: dict):
        # Analyze symptoms
        symptoms = self.symptom_analyzer(
            patient_description=patient_description,
            medical_history=patient_info.get('history', {})
        )
        
        # Generate diagnoses
        diagnoses = self.diagnosis_generator(
            symptoms=symptoms.symptoms,
            patient_age=patient_info.get('age', 0),
            medical_history=patient_info.get('history', {})
        )
        
        # Plan treatment for top diagnosis
        if diagnoses.possible_diagnoses:
            top_diagnosis = diagnoses.possible_diagnoses[0]
            treatment = self.treatment_planner(
                diagnosis=top_diagnosis['name'],
                patient_allergies=patient_info.get('allergies', []),
                current_medications=patient_info.get('medications', [])
            )
        else:
            treatment = None
        
        return dspy.Prediction(
            symptoms=symptoms.symptoms,
            severity=symptoms.severity,
            red_flags=symptoms.red_flags,
            diagnoses=diagnoses.possible_diagnoses,
            recommended_tests=diagnoses.recommended_tests,
            treatment_plan=treatment
        )`,
      testCases: [
        {
          input: 'Medical diagnosis support system',
          expected: true,
          description: 'Complete healthcare domain implementation',
        },
      ],
    },
    {
      id: 'dspy-m8-ex3',
      title: 'Research Application',
      description: 'Implement a simplified version of STORM for article generation',
      difficulty: 'expert',
      type: 'code',
      instructions: 'Create a system that researches topics from multiple perspectives.',
      hints: [
        'Generate different perspectives',
        'Research each perspective',
        'Synthesize findings',
      ],
      solution: `class SimpleSTORM(dspy.Module):
    def __init__(self, retriever):
        super().__init__()
        self.retriever = retriever
        
        self.perspective_generator = dspy.ChainOfThought(
            "topic: str -> perspectives: list[str], expert_roles: list[str]"
        )
        
        self.question_generator = dspy.ChainOfThought(
            "topic: str, perspective: str -> questions: list[str]"
        )
        
        self.section_writer = dspy.ChainOfThought(
            """topic: str,
            perspective: str,
            research_findings: list[str] ->
            section_title: str,
            section_content: str"""
        )
        
        self.article_synthesizer = dspy.ChainOfThought(
            "topic: str, sections: list[dict] -> introduction: str, conclusion: str"
        )
    
    def research_perspective(self, topic: str, perspective: str):
        # Generate questions
        questions = self.question_generator(
            topic=topic,
            perspective=perspective
        )
        
        # Research each question
        findings = []
        for q in questions.questions[:5]:  # Limit for efficiency
            results = self.retriever(q, k=3)
            findings.extend(results.passages)
        
        return findings
    
    def forward(self, topic: str):
        # Generate perspectives
        perspectives = self.perspective_generator(topic=topic)
        
        # Research and write sections
        sections = []
        for perspective in perspectives.perspectives[:3]:  # Top 3 perspectives
            findings = self.research_perspective(topic, perspective)
            
            section = self.section_writer(
                topic=topic,
                perspective=perspective,
                research_findings=findings
            )
            
            sections.append({
                'title': section.section_title,
                'content': section.section_content
            })
        
        # Synthesize article
        article = self.article_synthesizer(
            topic=topic,
            sections=sections
        )
        
        return dspy.Prediction(
            title=topic,
            introduction=article.introduction,
            sections=sections,
            conclusion=article.conclusion
        )`,
      testCases: [
        {
          input: 'STORM-like article generation system',
          expected: true,
          description: 'Multi-perspective research implementation',
        },
      ],
    },
    {
      id: 'dspy-m8-ex4',
      title: 'Privacy-Preserving System',
      description: 'Build a privacy-conscious AI system inspired by PAPILLON',
      difficulty: 'hard',
      type: 'code',
      instructions: 'Create a system that handles sensitive data with privacy preservation.',
      hints: ['Analyze privacy risks', 'Anonymize sensitive data', 'Use appropriate models'],
      solution: `class PrivacyPreservingAI(dspy.Module):
    def __init__(self, local_model, external_model):
        super().__init__()
        self.local_model = local_model
        self.external_model = external_model
        
        self.privacy_analyzer = dspy.ChainOfThought(
            """text: str ->
            contains_pii: bool,
            pii_types: list[str],
            risk_level: str"""
        )
        
        self.anonymizer = dspy.ChainOfThought(
            """text: str,
            pii_types: list[str] ->
            anonymized_text: str,
            replacements: dict"""
        )
        
        self.task_router = dspy.ChainOfThought(
            """task: str,
            risk_level: str ->
            use_local: bool,
            processing_strategy: str"""
        )
    
    def forward(self, text: str, task: str):
        # Analyze privacy risks
        privacy = self.privacy_analyzer(text=text)
        
        # Determine processing strategy
        routing = self.task_router(
            task=task,
            risk_level=privacy.risk_level
        )
        
        if routing.use_local or privacy.contains_pii:
            # Process locally or anonymize first
            if privacy.contains_pii:
                anonymized = self.anonymizer(
                    text=text,
                    pii_types=privacy.pii_types
                )
                safe_text = anonymized.anonymized_text
            else:
                safe_text = text
            
            # Use appropriate model
            if routing.use_local:
                with dspy.context(lm=self.local_model):
                    result = dspy.Predict("task, text -> result")(
                        task=task,
                        text=text  # Use original for local
                    )
            else:
                with dspy.context(lm=self.external_model):
                    result = dspy.Predict("task, text -> result")(
                        task=task,
                        text=safe_text  # Use anonymized for external
                    )
        else:
            # Low risk - use external directly
            with dspy.context(lm=self.external_model):
                result = dspy.Predict("task, text -> result")(
                    task=task,
                    text=text
                )
        
        return dspy.Prediction(
            result=result.result,
            privacy_preserved=privacy.contains_pii,
            processing_mode='local' if routing.use_local else 'external',
            risk_level=privacy.risk_level
        )`,
      testCases: [
        {
          input: 'Privacy-preserving AI system',
          expected: true,
          description: 'Complete privacy-conscious implementation',
        },
      ],
    },
  ],
  quiz: {
    id: 'dspy-m8-quiz',
    title: 'Module 8: Special Topics and Case Studies - Knowledge Check',
    description: 'Test your understanding of advanced DSPy applications',
    passingScore: 6,
    questions: [
      {
        id: 'q1',
        question: 'What type is used for image inputs in DSPy?',
        choices: ['dspy.ImageField', 'dspy.Image', 'dspy.Vision', 'dspy.Picture'],
        correctAnswer: 1,
        explanation:
          'DSPy uses the dspy.Image type for handling image inputs in multi-modal applications.',
      },
      {
        id: 'q2',
        question: 'Which model capability is required for audio processing in DSPy?',
        choices: [
          'Text generation only',
          'Image understanding',
          'Audio-capable models like gpt-4o-mini-audio-preview',
          'Video processing',
        ],
        correctAnswer: 2,
        explanation:
          'Audio processing requires models with audio capabilities, such as gpt-4o-mini-audio-preview.',
      },
      {
        id: 'q3',
        question: 'What is STORM designed to generate?',
        choices: [
          'Code documentation',
          'Wikipedia-like articles through multi-perspective research',
          'Test cases',
          'API specifications',
        ],
        correctAnswer: 1,
        explanation:
          'STORM generates comprehensive Wikipedia-like articles by researching topics from multiple perspectives.',
      },
      {
        id: 'q4',
        question: 'What is the key innovation of PAPILLON?',
        choices: [
          'Faster processing',
          'Privacy preservation through local-external model collaboration',
          'Better accuracy',
          'Lower costs',
        ],
        correctAnswer: 1,
        explanation:
          'PAPILLON innovates by combining local and external models to preserve privacy while maintaining capability.',
      },
      {
        id: 'q5',
        question: 'How does JetBlue use DSPy in production?',
        choices: [
          'For flight path optimization',
          'For multiple chatbot use cases in customer service',
          'For pricing strategies',
          'For maintenance scheduling',
        ],
        correctAnswer: 1,
        explanation:
          'JetBlue uses DSPy to power multiple chatbot use cases, improving customer service efficiency.',
      },
      {
        id: 'q6',
        question: 'What does Replit use DSPy for?',
        choices: [
          'User authentication',
          'Code diff synthesis for their AI coding assistant',
          'Database management',
          'Payment processing',
        ],
        correctAnswer: 1,
        explanation:
          'Replit uses DSPy to synthesize code diffs for their AI-powered coding assistant.',
      },
      {
        id: 'q7',
        question: "What is Haize Labs' application of DSPy?",
        choices: [
          'Content moderation',
          'Language translation',
          'Automated red-teaming of language models',
          'Data analysis',
        ],
        correctAnswer: 2,
        explanation:
          'Haize Labs uses DSPy for automated red-teaming to test and improve language model safety.',
      },
      {
        id: 'q8',
        question: 'When building domain-specific DSPy applications, what is crucial?',
        choices: [
          'Using the largest models available',
          'Collaborating with domain experts for validation',
          'Minimizing the number of modules',
          'Avoiding specialized terminology',
        ],
        correctAnswer: 1,
        explanation:
          'Domain expertise is crucial for understanding requirements, validating outputs, and ensuring practical applicability.',
      },
    ],
  },
};
